import React, { useState } from "react";
import styles from "./Home.module.css";
import Panel from "./Panel";
import SegmentedControl from "../../../components/common/SegmentedControl";
import { useCurrency } from "../../../hooks/useCurrency";
import { AT_RISK_BURN, budgetRows } from "./homeData";
import { formatDay } from "../../../utils/weekDates";

const tone = (burn) => (burn > 1 ? "Red" : burn >= AT_RISK_BURN ? "Amber" : "");

// One row per project, fullest burn first: a bar for actual use with a tick for what is scheduled.
export default function ProjectBudgets({ overview, onOpenProject }) {
  const [metric, setMetric] = useState("time");
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const { format } = useCurrency();
  const money = metric === "cost";
  const fmt = (n) => (money ? format(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 }) : `${Math.round(n * 10) / 10}h`);
  const all = budgetRows(overview, metric);
  const rows = atRiskOnly ? all.filter((r) => r.atRisk) : all;

  return (
    <Panel
      title="Project budgets"
      count={rows.length}
      countTone="neutral"
      tools={
        <>
          <label className={styles.checkLabel}><input type="checkbox" checked={atRiskOnly} onChange={(e) => setAtRiskOnly(e.target.checked)} /> At risk only</label>
          <SegmentedControl ariaLabel="Time or cost" value={metric} onChange={setMetric} options={[{ value: "time", label: "Time" }, { value: "cost", label: "Cost" }]} />
        </>
      }
    >
      {rows.length === 0 ? (
        <div className={styles.empty}>{atRiskOnly ? "No projects at risk." : "No projects yet."}</div>
      ) : (
        <div className={styles.budgetScroll}>
        <table className={styles.table}>
          <thead>
            <tr><th>Project</th><th>Status</th><th>Due</th><th>Burn (actual · scheduled)</th><th className={styles.right}>Used / budget</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const t = tone(r.burn);
              return (
                <tr key={r.project_id} onClick={() => onOpenProject(r.project_id)}>
                  <td><div className={styles.projName}>{r.project_name}</div><div className={styles.projClient}>{r.client_company}</div></td>
                  <td><span className={styles.statusPill} style={r.status_color ? { background: `${r.status_color}22`, color: r.status_color } : undefined}>{r.status_name}</span></td>
                  <td className={r.is_overdue ? styles.overdueText : undefined}>{r.end_date ? formatDay(r.end_date.slice(0, 10)) : "—"}</td>
                  <td>
                    <div className={styles.burnCell}>
                      <div className={`${styles.burn} ${r.burn === null ? styles.burnNone : ""}`}>
                        {r.burn !== null && <div className={`${styles.burnFill} ${styles[`burn${t}`] || ""}`} style={{ width: `${Math.min(100, r.burn * 100)}%` }} />}
                        {r.burn !== null && <span className={styles.burnTick} style={{ left: `calc(${Math.min(100, (r.scheduled / r.budget) * 100)}% - 1px)` }} title={`Scheduled ${fmt(r.scheduled)}`} />}
                      </div>
                      <span className={`${styles.burnPct} ${styles[`pct${t}`] || ""}`}>{r.burn === null ? "—" : `${Math.round(r.burn * 100)}%`}</span>
                    </div>
                  </td>
                  <td className={styles.right}><b>{fmt(r.actual)}</b> / {r.budget > 0 ? fmt(r.budget) : `no ${money ? "cost" : "time"} budget`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </Panel>
  );
}
